import { useNavigate } from "react-router-dom";
import { colors, fontSizes, radii } from "../styles/theme";

// UTC-safe: ISO strings parse as UTC, Date.now() is UTC ms — no timezone drift.
function daysUntilExpiry(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

interface Props {
  tierExpiresAt: string | null | undefined;
  status: string | undefined;
}

export function TrialBanner({ tierExpiresAt, status }: Props) {
  const navigate = useNavigate();

  if (status === "tier_expired") return null; // expired gate handled separately

  if (!tierExpiresAt) return null;

  const days = daysUntilExpiry(tierExpiresAt);

  if (days <= 0) return null; // expired — gate will handle it

  const urgent  = days <= 2;
  const warning = !urgent && days < 7;

  // #3115 / #3153 — was hardcoded rgba() + dark-mode hex (colors.error,
  // colors.warning, colors.goldSecondary) for both bg/border/fg. On light
  // mode this rendered yellow-amber text on cream-amber background — the
  // "Upgrade to Tier 1" CTA the founder flagged. We now key off brand
  // status tokens which carry per-mode text colour pairs (the *-text token
  // is AA-tuned for the *-bg surface in BOTH modes), and the CTA picks
  // up a dark fg against the gold bg via --brand-accent-secondary-fg.
  type Tone = "error" | "warning" | "info";
  const tone: Tone = urgent ? "error" : warning ? "warning" : "info";
  const bgColor: string = tone === "error"
    ? "var(--brand-status-error-bg)"
    : tone === "warning"
      ? "var(--brand-status-warning-bg)"
      : "var(--brand-status-warning-bg)";
  const borderColor: string = tone === "error"
    ? "var(--brand-status-error)"
    : tone === "warning"
      ? "var(--brand-status-warning)"
      : "var(--brand-accent-secondary)";
  const textColor: string = tone === "error"
    ? "var(--brand-status-error-text)"
    : tone === "warning"
      ? "var(--brand-status-warning-text)"
      : "var(--brand-status-warning-text)";

  const label = urgent
    ? `Your free trial ends in ${days} day${days === 1 ? "" : "s"}.`
    : `Your free trial ends in ${days} days.`;

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      flexWrap: "wrap", gap: "0.5rem",
      background: bgColor,
      borderBottom: `1px solid ${borderColor}`,
      padding: "0.6rem 1.25rem",
      fontSize: fontSizes.sm,
      color: textColor,
    }}>
      <span>
        <strong>{label}</strong>
        {urgent && " Upgrade now to keep your agents running."}
      </span>
      <button
        onClick={() => navigate("/upgrade")}
        style={{
          // CTA: solid gold bg with the dark ink token. Used to be a tinted
          // washed-out variant of borderColor which collapsed to "amber on
          // cream amber" in light mode (≈ 1.4:1, well under AA).
          background: "var(--brand-accent-secondary)",
          border: "1px solid var(--brand-accent-secondary)",
          borderRadius: radii.sm,
          color: "var(--brand-accent-secondary-fg)",   // dark ink on amber (~8.2:1)
          padding: "0.3rem 0.85rem",
          fontSize: fontSizes.xs,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit",
          whiteSpace: "nowrap",
        }}
      >
        Upgrade to Tier 1 →
      </button>
    </div>
  );
}

export function TierExpiredBlock() {
  const navigate = useNavigate();
  return (
    <div style={{
      padding: "3rem 1.5rem", display: "flex", justifyContent: "center",
    }}>
      <div style={{
        background: "var(--brand-bg-elevated)",
        border: "1px solid var(--brand-status-error)",
        borderRadius: radii.lg,
        padding: "2rem 2.25rem",
        maxWidth: 440,
        width: "100%",
        textAlign: "center",
        display: "flex", flexDirection: "column", gap: 16,
      }}>
        <div style={{ fontSize: "2rem" }}>⏰</div>
        <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--brand-text-primary)" }}>
          Your free trial has ended
        </div>
        <div style={{ fontSize: fontSizes.sm, color: "var(--brand-text-secondary)", lineHeight: 1.6 }}>
          Upgrade to Tier 1 to continue using 2hives.ai.
          Your hives and configuration are safe — they'll be waiting for you.
        </div>
        <button
          onClick={() => navigate("/upgrade")}
          style={{
            padding: "0.75rem 1.5rem",
            // Gold gradient bg is safe in both modes (--brand-accent →
            // --brand-accent-secondary). The legacy `var(--brand-bg-default)`
            // fg was DARK green in dark mode (~11.7:1, perfect) but LIGHT
            // in light mode (~1.2:1, AA fail). The new --brand-accent-fg
            // token stays dark in both modes (the gold doesn't shift).
            background: `linear-gradient(135deg, ${colors.goldPrimary}, ${colors.goldSecondary})`,
            border: "none", borderRadius: radii.sm,
            color: "var(--brand-accent-fg)", fontWeight: 700, fontSize: fontSizes.sm,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Upgrade Now →
        </button>
        <button
          onClick={() => navigate("/billing")}
          style={{
            background: "transparent",
            border: "1px solid var(--brand-border-strong)",
            borderRadius: radii.sm,
            color: "var(--brand-text-secondary)", fontSize: fontSizes.sm,
            padding: "0.55rem", cursor: "pointer", fontFamily: "inherit",
          }}
        >
          View billing
        </button>
      </div>
    </div>
  );
}
